import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiEye, FiEdit2, FiDownload, FiCreditCard, FiCopy, FiXCircle, FiTrash2, FiFileText, FiAlertCircle,
  FiX, FiRotateCcw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, ConfirmDialog } from '../../components/common';
import ClientSearchAutocomplete from './components/ClientSearchAutocomplete';
import RecordPaymentModal from './components/RecordPaymentModal';
import CancelInvoiceModal from './components/CancelInvoiceModal';
import { invoiceService } from '../../services/invoiceService';
import { billingFiles } from '../../services/billingFiles';
import { getApiError } from '../../services/cmsService';
import { useDebounce } from '../../hooks';
import { exportToCSV } from '../../utils/exportUtils';
import {
  INVOICE_STATUSES, InvoiceStatusBadge, financialYearOptions, formatDMY, formatINR,
} from './billingUtils';
import './Billing.css';
import './InvoicesListPage.css';

const PER_PAGE = 15;
const EXPORT_LIMIT = 1000;

const statusLabel = (value) => INVOICE_STATUSES.find((s) => s.value === value)?.label ?? value;

const InvoicesListPage = () => {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [client, setClient] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [financialYear, setFinancialYear] = useState('');
  const [overdue, setOverdue] = useState(false);
  // DataTable keeps its own copy of the search text; remount it to clear that.
  const [tableKey, setTableKey] = useState(0);
  const debouncedSearch = useDebounce(search, 350);

  const [exporting, setExporting] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reinstateTarget, setReinstateTarget] = useState(null);
  const [reinstating, setReinstating] = useState(false);

  const filterParams = useCallback(() => {
    const params = {};
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (statuses.length) params.status = statuses.join(',');
    if (client) params.billing_client_id = client.id;
    if (from) params.from = from;
    if (to) params.to = to;
    if (financialYear) params.financial_year = financialYear;
    if (overdue) params.overdue = 1;
    return params;
  }, [debouncedSearch, statuses, client, from, to, financialYear, overdue]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    invoiceService
      .getAll({ ...filterParams(), page, per_page: PER_PAGE })
      .then((res) => {
        if (cancelled) return;
        setInvoices(res.items);
        setTotal(res.total);
        setSummary(res.summary);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getApiError(error, 'Failed to load invoices'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filterParams, page, reloadKey]);

  // Any filter change goes back to the first page
  useEffect(() => {
    setPage(1);
  }, [filterParams]);

  const reload = () => setReloadKey((k) => k + 1);

  const replaceRow = (updated) => {
    setInvoices((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated, itemsCount: row.itemsCount } : row)));
    reload(); // summary figures changed too
  };

  const toggleStatus = (value) =>
    setStatuses((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));

  const hasFilters = Boolean(statuses.length || client || from || to || financialYear || overdue || search);

  const clearFilters = () => {
    setStatuses([]);
    setClient(null);
    setFrom('');
    setTo('');
    setFinancialYear('');
    setOverdue(false);
    setSearch('');
    setTableKey((k) => k + 1);
  };

  const runRowAction = async (row, key, action) => {
    setRowBusy(`${row.id}:${key}`);
    try {
      await action();
    } catch (error) {
      toast.error(getApiError(error, 'Something went wrong'));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDownload = (row) =>
    runRowAction(row, 'pdf', async () => {
      // The list row has no download_filename; the detail response does.
      const detail = await invoiceService.getById(row.id);
      await billingFiles.downloadPdf(row.id, detail.downloadFilename);
    });

  const handleDuplicate = (row) =>
    runRowAction(row, 'dup', async () => {
      const copy = await invoiceService.duplicate(row.id);
      toast.success('Duplicate draft created');
      navigate(`/billing/invoices/${copy.id}/edit`);
    });

  const handleDelete = async () => {
    const isDraft = deleteTarget.status === 'draft';
    setDeleting(true);
    try {
      await invoiceService.delete(deleteTarget.id);
      toast.success(isDraft ? 'Draft deleted' : `Invoice ${deleteTarget.number} deleted`);
      reload();
    } catch (error) {
      toast.error(getApiError(error, isDraft ? 'Failed to delete the draft' : 'Failed to delete the invoice'));
    } finally {
      setDeleting(false);
    }
  };

  const handleReinstate = async () => {
    setReinstating(true);
    try {
      const updated = await invoiceService.reinstate(reinstateTarget.id);
      toast.success(`Invoice ${updated.number} reinstated`);
      replaceRow(updated);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to reinstate the invoice'));
    } finally {
      setReinstating(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await invoiceService.getAll({ ...filterParams(), page: 1, per_page: EXPORT_LIMIT });
      if (!res.items.length) {
        toast.info('Nothing to export for these filters');
        return;
      }
      exportToCSV(
        res.items.map((row) => ({
          'Invoice No': row.number || 'Draft',
          Date: formatDMY(row.invoiceDate),
          'Due date': formatDMY(row.dueDate),
          Client: row.clientName,
          'Client ID': row.clientCode,
          GSTIN: row.clientGstin,
          Taxable: row.taxableAmount,
          CGST: row.cgstAmount,
          SGST: row.sgstAmount,
          IGST: row.igstAmount,
          Total: row.totalAmount,
          Received: row.receivedAmount,
          Balance: row.balanceAmount,
          Status: statusLabel(row.status),
          Overdue: row.isOverdue ? 'Yes' : '',
        })),
        `invoices-${financialYear || 'all'}.csv`
      );
      if (res.total > res.items.length) {
        toast.warn(`Exported the first ${res.items.length} of ${res.total} invoices. Narrow the filters to export the rest.`);
      }
    } catch (error) {
      toast.error(getApiError(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const busy = (row, key) => rowBusy === `${row.id}:${key}`;

  const columns = [
    {
      header: 'Invoice No',
      width: '120px',
      render: (row) => (
        <button type="button" className="bl-link" onClick={() => navigate(`/billing/invoices/${row.id}`)}>
          {row.number || <span className="bl-muted">Draft</span>}
        </button>
      ),
    },
    { header: 'Date', width: '100px', render: (row) => <span className="bl-amount">{formatDMY(row.invoiceDate)}</span> },
    {
      header: 'Client',
      render: (row) => (
        <span className="bl-cell-stack">
          <span>{row.clientName || '—'}</span>
          {row.clientCode && <span className="bl-cell-sub">{row.clientCode}</span>}
        </span>
      ),
    },
    { header: 'Taxable', align: 'right', render: (row) => <span className="bl-amount">{formatINR(row.taxableAmount)}</span> },
    { header: 'Tax', align: 'right', render: (row) => <span className="bl-amount">{formatINR(row.taxAmount)}</span> },
    { header: 'Total', align: 'right', render: (row) => <strong className="bl-amount">{formatINR(row.totalAmount)}</strong> },
    { header: 'Received', align: 'right', render: (row) => <span className="bl-amount">{formatINR(row.receivedAmount)}</span> },
    {
      header: 'Balance',
      align: 'right',
      render: (row) => (
        <span className={`bl-amount ${row.isOverdue ? 'il-overdue' : ''}`}>{formatINR(row.balanceAmount)}</span>
      ),
    },
    { header: 'Status', render: (row) => <InvoiceStatusBadge status={row.status} isOverdue={row.isOverdue} /> },
    {
      header: 'Actions',
      width: '220px',
      align: 'center',
      render: (row) => (
        <div className="bl-actions">
          <button type="button" className="action-btn view" title="View" onClick={() => navigate(`/billing/invoices/${row.id}`)}>
            <FiEye />
          </button>
          {row.permissions.canEdit && (
            <button type="button" className="action-btn edit" title="Edit draft" onClick={() => navigate(`/billing/invoices/${row.id}/edit`)}>
              <FiEdit2 />
            </button>
          )}
          <button type="button" className="action-btn" title="Download PDF" disabled={busy(row, 'pdf')} onClick={() => handleDownload(row)}>
            <FiDownload />
          </button>
          {row.permissions.canRecordPayment && (
            <button type="button" className="action-btn view" title="Record payment" onClick={() => setPaymentTarget(row)}>
              <FiCreditCard />
            </button>
          )}
          <button type="button" className="action-btn" title="Duplicate as new draft" disabled={busy(row, 'dup')} onClick={() => handleDuplicate(row)}>
            <FiCopy />
          </button>
          {row.permissions.canCancel && (
            <button type="button" className="action-btn warn" title="Cancel invoice" onClick={() => setCancelTarget(row)}>
              <FiXCircle />
            </button>
          )}
          {row.permissions.canReinstate && (
            <button type="button" className="action-btn view" title="Reinstate (undo cancel)" onClick={() => setReinstateTarget(row)}>
              <FiRotateCcw />
            </button>
          )}
          {row.permissions.canDelete && (
            <button
              type="button"
              className="action-btn delete"
              title={row.status === 'draft' ? 'Delete draft' : 'Delete cancelled invoice'}
              onClick={() => setDeleteTarget(row)}
            >
              <FiTrash2 />
            </button>
          )}
        </div>
      ),
    },
  ];

  const fyOptions = financialYearOptions(5);

  return (
    <div className="bl-page invoices-list">
      <div className="bl-stats">
        <div className="bl-stat">
          <span className="bl-stat-label"><FiFileText /> Invoiced {summary?.financialYear && `· FY ${summary.financialYear}`}</span>
          <span className="bl-stat-value">{summary ? formatINR(summary.totalInvoiced) : '—'}</span>
          <span className="bl-stat-hint">{summary ? `${summary.invoiceCount} issued invoice${summary.invoiceCount === 1 ? '' : 's'}` : ' '}</span>
        </div>
        <div className="bl-stat tone-success">
          <span className="bl-stat-label">Received</span>
          <span className="bl-stat-value">{summary ? formatINR(summary.received) : '—'}</span>
        </div>
        <div className="bl-stat tone-warning">
          <span className="bl-stat-label">Outstanding</span>
          <span className="bl-stat-value">{summary ? formatINR(summary.outstanding) : '—'}</span>
        </div>
        <button
          type="button"
          className={`bl-stat tone-orange ${overdue ? 'is-active' : ''}`}
          onClick={() => setOverdue((v) => !v)}
          aria-pressed={overdue}
          title={overdue ? 'Show all invoices' : 'Show overdue invoices only'}
        >
          <span className="bl-stat-label"><FiAlertCircle /> Overdue</span>
          <span className="bl-stat-value">{summary ? summary.overdueCount : '—'}</span>
          <span className="bl-stat-hint">{overdue ? 'Filter on — click to clear' : 'All years · click to filter'}</span>
        </button>
      </div>

      <Card
        title="Invoices"
        subtitle={summary ? `${total} matching · ${summary.draftCount} draft${summary.draftCount === 1 ? '' : 's'}` : undefined}
        action={
          <div className="bl-header-actions">
            <Button variant="outline" icon={<FiDownload />} onClick={handleExport} loading={exporting}>
              Export CSV
            </Button>
            <Button icon={<FiPlus />} onClick={() => navigate('/billing/invoices/new')}>
              New invoice
            </Button>
          </div>
        }
      >
        <div className="bl-filters">
          <div className="bl-filter-field grow">
            <span className="bl-filter-label">Client</span>
            <ClientSearchAutocomplete compact selected={client} onSelect={setClient} onClear={() => setClient(null)} placeholder="Any client" />
          </div>
          <label className="bl-filter-field">
            <span className="bl-filter-label">Financial year</span>
            <span className="bl-filter">
              <select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)}>
                <option value="">All years</option>
                {fyOptions.map((fy) => <option key={fy} value={fy}>FY {fy}</option>)}
              </select>
            </span>
          </label>
          <label className="bl-filter-field">
            <span className="bl-filter-label">From</span>
            <span className="bl-filter">
              <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </span>
          </label>
          <label className="bl-filter-field">
            <span className="bl-filter-label">To</span>
            <span className="bl-filter">
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </span>
          </label>
        </div>

        <div className="il-status-row">
          <div className="bl-chip-group" role="group" aria-label="Filter by status">
            {INVOICE_STATUSES.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`bl-chip ${statuses.includes(s.value) ? 'is-active' : ''}`}
                aria-pressed={statuses.includes(s.value)}
                onClick={() => toggleStatus(s.value)}
              >
                {s.label}
              </button>
            ))}
            {overdue && (
              <button type="button" className="bl-chip is-active" onClick={() => setOverdue(false)}>
                Overdue <FiX />
              </button>
            )}
          </div>
          {hasFilters && (
            <Button variant="ghost" size="small" icon={<FiX />} onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>

        <DataTable
          key={tableKey}
          columns={columns}
          data={invoices}
          loading={loading}
          searchable
          searchPlaceholder="Search number, client, GSTIN or client ID…"
          searchValue={search}
          onSearch={setSearch}
          pagination
          pageSize={PER_PAGE}
          totalItems={total}
          currentPage={page}
          onPageChange={setPage}
          emptyMessage={hasFilters ? 'No invoices match these filters.' : 'No invoices yet. Create the first one.'}
        />
      </Card>

      <RecordPaymentModal
        isOpen={Boolean(paymentTarget)}
        onClose={() => setPaymentTarget(null)}
        invoice={paymentTarget}
        onRecorded={replaceRow}
      />

      <CancelInvoiceModal
        isOpen={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        invoice={cancelTarget}
        onCancelled={replaceRow}
      />

      <ConfirmDialog
        isOpen={Boolean(reinstateTarget)}
        onClose={() => setReinstateTarget(null)}
        onConfirm={handleReinstate}
        title={`Reinstate invoice ${reinstateTarget?.number ?? ''}?`}
        message="This undoes the cancellation. The invoice goes back to issued with the same number, and its projects are marked invoiced again."
        confirmText="Reinstate invoice"
        variant="primary"
        loading={reinstating}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={deleteTarget?.status === 'draft' ? 'Delete draft' : `Delete invoice ${deleteTarget?.number ?? ''}`}
        message={
          deleteTarget?.status === 'draft'
            ? `Delete the draft for ${deleteTarget?.clientName || 'this client'}? Its projects stay pending.`
            : `Delete cancelled invoice ${deleteTarget?.number ?? ''}? It disappears from the invoice list and can no longer be reinstated. Its number is not reused.`
        }
        confirmText={deleteTarget?.status === 'draft' ? 'Delete draft' : 'Delete invoice'}
        loading={deleting}
      />
    </div>
  );
};

export default InvoicesListPage;
